import React, { useState } from "react";

import { Loader2, CreditCard, Plus, Check } from "lucide-react";

import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { PaymentMethod } from "../../../lib/api/billing";

interface AutoReplenishModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  enabled: boolean;
  loading: boolean;
  saving: boolean;
  paymentMethods: PaymentMethod[];
  selectedCardId: string | null;
  onSelectCard: (cardId: string) => void;
  threshold: number;
  onThresholdChange: (value: number) => void;
  amount: number;
  onAmountChange: (value: number) => void;
  onAddCard: () => void;
  onSave: () => void;
}

const formatCardBrand = (brand: string): string => {
  const brands: Record<string, string> = {
    visa: "Visa",
    mastercard: "Mastercard",
    amex: "American Express",
    discover: "Discover",
    diners: "Diners Club",
    jcb: "JCB",
    unionpay: "UnionPay",
  };
  return brands[brand.toLowerCase()] || brand;
};

export const AutoReplenishModal: React.FC<AutoReplenishModalProps> = ({
  open,
  onOpenChange,
  enabled,
  loading,
  saving,
  paymentMethods,
  selectedCardId,
  onSelectCard,
  threshold,
  onThresholdChange,
  amount,
  onAmountChange,
  onAddCard,
  onSave,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {enabled ? "Edit Auto-Replenish Settings" : "Enable Auto-Replenish"}
          </DialogTitle>
          <DialogDescription>
            Automatically add funds when your balance drops below a threshold.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-4 py-4">
            {/* Payment Method Selection */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Payment Method</Label>
              {paymentMethods.length === 0 ? (
                <div className="p-4 border border-dashed rounded-md text-center">
                  <CreditCard className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground mb-3">
                    No payment methods saved
                  </p>
                  <Button onClick={onAddCard} variant="outline" size="sm">
                    <Plus className="h-4 w-4 mr-2" />
                    Add a Card
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {paymentMethods.map((pm) => (
                    <button
                      key={pm.id}
                      type="button"
                      onClick={() => onSelectCard(pm.id)}
                      className={`w-full flex items-center justify-between p-3 rounded-md border transition-colors ${
                        selectedCardId === pm.id
                          ? "border-foreground bg-foreground/5"
                          : "border-border hover:border-foreground/50"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <CreditCard className="h-5 w-5 text-muted-foreground" />
                        <div className="text-left">
                          <p className="text-sm font-medium">
                            {formatCardBrand(pm.brand)} ****{pm.last4}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Expires {pm.expMonth}/{pm.expYear}
                            {pm.isSubscriptionDefault && " (Subscription card)"}
                          </p>
                        </div>
                      </div>
                      {selectedCardId === pm.id && (
                        <Check className="h-5 w-5 text-foreground" />
                      )}
                    </button>
                  ))}
                  <Button
                    onClick={onAddCard}
                    variant="ghost"
                    size="sm"
                    className="w-full"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add New Card
                  </Button>
                </div>
              )}
            </div>

            {/* Threshold */}
            <div className="space-y-2">
              <Label htmlFor="modal-threshold" className="text-sm font-medium">
                When balance drops below
              </Label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">$</span>
                <Input
                  id="modal-threshold"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={threshold === 0 ? '' : threshold}
                  onChange={(e) => {
                    const rawValue = e.target.value.replace(/[^0-9]/g, '');
                    onThresholdChange(rawValue === '' ? 0 : parseInt(rawValue, 10));
                  }}
                  className={`w-24 ${threshold > 100 ? 'border-red-500' : ''}`}
                />
              </div>
              {threshold > 100 && (
                <p className="text-xs text-red-500">Maximum threshold is $100</p>
              )}
            </div>

            {/* Amount */}
            <div className="space-y-2">
              <Label htmlFor="modal-amount" className="text-sm font-medium">
                Add this amount
              </Label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">$</span>
                <Input
                  id="modal-amount"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={amount === 0 ? '' : amount}
                  onChange={(e) => {
                    const rawValue = e.target.value.replace(/[^0-9]/g, '');
                    onAmountChange(rawValue === '' ? 0 : parseInt(rawValue, 10));
                  }}
                  className={`w-24 ${amount > 500 || (amount > 0 && amount < 5) ? 'border-red-500' : ''}`}
                />
              </div>
              {amount > 500 && (
                <p className="text-xs text-red-500">Maximum amount is $500</p>
              )}
              {amount > 0 && amount < 5 && (
                <p className="text-xs text-red-500">Minimum amount is $5</p>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={onSave}
            disabled={saving || !selectedCardId || paymentMethods.length === 0 || threshold > 100 || amount > 500 || amount < 5}
            className="bg-foreground hover:bg-foreground/90 text-background"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : enabled ? (
              "Save Changes"
            ) : (
              "Enable"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
